import { useState } from 'react'
import { WorkspaceService } from 'api/services/WorkspaceService'
import { CanView } from 'components/Can'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { observer } from 'mobx-react-lite'
import { useSelector } from 'react-redux'
import { RootState, UserRole } from 'types'
import Styles from './NavMenu.module.scss'
import { NavMenuHomeItem } from './Buttons/NavMenuHomeItem'
import { NavMenuBrandKitsItem } from 'blaze-components/WorkspaceDashboardSidebar/components/NavMenu/Buttons/NavMenuBrandKitsItem'
import { NavMenuTodosItem } from './Buttons/NavMenuTodosItem'
import { NavMenuSharedWithMeItem } from './Buttons/NavMenuSharedWithMeItem'
import { NavMenuContentPreferencesItem } from './Buttons/NavMenuContentPreferencesItem'
import { ExpandableTree, useExpandable } from 'blaze-components/WorkspaceDashboardSidebar/hooks/useExpandable'
import { isToday } from 'utils/dates'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useIsIntegrationsV3 } from 'hooks/useIsIntegrationsV3'
import { NavMenuTemplateBindersItem } from './Buttons/NavMenuTemplateBindersItem'
import { NavMenuTemplatesPageItem } from './Buttons/NavMenuTemplatesPageItem'
import { NavMenuAnalyticsItem } from './Buttons/NavMenuAnalyticsItem'
import { NavMenuAutopilotIntegrationsItem } from './Buttons/NavMenuAutopilotIntegrationsItem'
import { NavMenuScheduledPostsItem } from './Buttons/NavMenuScheduledPostsItem'
import { NavMenuCampaignsItem } from './Buttons/NavMenuCampaignsItem'
import { NavMenuBrainPlaygroundItem } from './Buttons/NavMenuBrainPlaygroundItem'
import { NavMenuGenerationV2PlaygroundItem } from './Buttons/NavMenuGenerationV2PlaygroundItem'
import { NavMenuLearningLoopPlaygroundItem } from './Buttons/NavMenuLearningLoopPlaygroundItem'
import { NavMenuLearningsItem } from './Buttons/NavMenuLearningsItem'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { NavMenuApprovalsItem } from './Buttons/NavMenuApprovalsItem'
import { NavMenuInsightsItem } from './Buttons/NavMenuInsightsItem'
import { NavMenuArchiveItem } from './Buttons/NavMenuArchiveItem'

const NavMenu = observer(() => {
  const workspace = useCurrentWorkspace()
  const { toggle, expanded } = useExpandable(ExpandableTree.Configure)
  const { isMobile } = useResponsiveDesign()
  const canSeeTemplateBinders = useFlipperFlag('admin_templates')
  const canSeeIntegrations = useIsIntegrationsV3()
  const canSeeBrainPlayground = useFlipperFlag('brain_playground')
  const canSeeFullGenPlayground = useFlipperFlag('generation_playground')
  const canSeeLearningLoopPlayground = useFlipperFlag('learning_loop_playground')
  const canSeeLearnings = useFlipperFlag('learnings_screen')
  const canSeeInsights = useFlipperFlag('new_home_page') && workspace?.is_virtual_marketer
  const canSeeArchive = useFlipperFlag('archive_page')

  const canSeeAnalytics = !workspace?.is_virtual_marketer

  const { sidebarLocked, floatingSidebar } = useSelector(
    ({ sidebar: { locked: sidebarLocked }, floatingSidebar }: RootState) => ({
      floatingSidebar,
      sidebarLocked,
    }),
  )

  const [hasToggledOpen, setHasToggledOpen] = useState(false)

  if (!hasToggledOpen && workspace && isToday(workspace.created_at) && !expanded) {
    toggle()
    setHasToggledOpen(true)
  }

  const closeMobileSidebar = () => {
    if (isMobile && (sidebarLocked || floatingSidebar)) WorkspaceService.hideSidebar()
  }

  if (!workspace) return null

  if (workspace.is_virtual_marketer) {
    return (
      <>
        <ResponsiveDesign.Desktop>
          <ul className={Styles.navItemsList}>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuHomeItem workspace={workspace} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuScheduledPostsItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuCampaignsItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            {canSeeIntegrations && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuAutopilotIntegrationsItem onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuBrandKitsItem workspace={workspace} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuContentPreferencesItem workspace={workspace} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuApprovalsItem workspace={workspace} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            {canSeeArchive && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuArchiveItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            {canSeeLearnings && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuLearningsItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            {canSeeInsights && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuInsightsItem workspace={workspace} onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            {canSeeAnalytics && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuAnalyticsItem workspace={workspace} onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            {canSeeBrainPlayground && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuBrainPlaygroundItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            {canSeeFullGenPlayground && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuGenerationV2PlaygroundItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            {canSeeLearningLoopPlayground && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuLearningLoopPlaygroundItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            <CanView as={[UserRole.GUEST]} inWorkspace={workspace.id}>
              <li>
                <NavMenuSharedWithMeItem workspace={workspace} onPress={closeMobileSidebar} />
              </li>
            </CanView>
          </ul>
        </ResponsiveDesign.Desktop>
        <ResponsiveDesign.Mobile>
          <ul className={Styles.navItemsList}>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuHomeItem workspace={workspace} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuScheduledPostsItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuCampaignsItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
              </li>
            </CanView>

            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuBrandKitsItem workspace={workspace} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            {canSeeLearnings && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuLearningsItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            {canSeeIntegrations && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuAutopilotIntegrationsItem onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
          </ul>
        </ResponsiveDesign.Mobile>
      </>
    )
  }

  return (
    <ul className={Styles.navItemsList}>
      <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
        <li>
          <NavMenuHomeItem workspace={workspace} onPress={closeMobileSidebar} />
        </li>
      </CanView>
      <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
        <li>
          <NavMenuBrandKitsItem workspace={workspace} onPress={closeMobileSidebar} />
        </li>
      </CanView>
      <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
        <li>
          <NavMenuTemplatesPageItem workspace={workspace} onPress={closeMobileSidebar} />
        </li>
      </CanView>
      <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
        <li>
          <NavMenuScheduledPostsItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
        </li>
      </CanView>
      <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
        <li>
          <NavMenuCampaignsItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
        </li>
      </CanView>
      {canSeeAnalytics && (
        <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
          <li>
            <NavMenuAnalyticsItem workspace={workspace} onPress={closeMobileSidebar} />
          </li>
        </CanView>
      )}
      <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
        <li>
          <NavMenuTodosItem workspace={workspace} onPress={closeMobileSidebar} />
        </li>
      </CanView>
      {canSeeBrainPlayground && (
        <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
          <li>
            <NavMenuBrainPlaygroundItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
          </li>
        </CanView>
      )}
      {canSeeFullGenPlayground && (
        <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
          <li>
            <NavMenuGenerationV2PlaygroundItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
          </li>
        </CanView>
      )}
      <CanView as={[UserRole.GUEST]} inWorkspace={workspace.id}>
        <li>
          <NavMenuSharedWithMeItem workspace={workspace} onPress={closeMobileSidebar} />
        </li>
      </CanView>
      {canSeeTemplateBinders && (
        <li>
          <NavMenuTemplateBindersItem workspace={workspace} onPress={closeMobileSidebar} />
        </li>
      )}
    </ul>
  )
})

export { NavMenu }
